/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ActionComponent,
  ActionComponent$inboundSchema,
  ActionComponent$Outbound,
  ActionComponent$outboundSchema,
} from "./actioncomponent.js";
import {
  Animatic,
  Animatic$inboundSchema,
  Animatic$Outbound,
  Animatic$outboundSchema,
} from "./animatic.js";
import {
  DisplayRef,
  DisplayRef$inboundSchema,
  DisplayRef$Outbound,
  DisplayRef$outboundSchema,
} from "./displayref.js";
import {
  EBIcon,
  EBIcon$inboundSchema,
  EBIcon$Outbound,
  EBIcon$outboundSchema,
} from "./ebicon.js";

/**
 * The configuration of a summary of an equipment browser item
 */
export type SummaryConfig = {
  /**
   * The actions to display on the summary
   */
  actionComponents: Array<ActionComponent>;
  /**
   * The alias of the item that is being summarized.
   */
  alias: string;
  /**
   * Defines the configuration of an animatic. The Animatic is made up of a primitive component, and optional labels, gauges, and a shelf.
   */
  animatic: Animatic;
  /**
   * A single icon to display
   */
  deviceIcon: EBIcon;
  /**
   * The metrics to display on the summary
   */
  displayRefs: Array<DisplayRef>;
  /**
   * The kind for this summary item
   */
  kind?: string | undefined;
  /**
   * The reference id of the item that is being summarized.
   */
  referenceId: string;
  /**
   * The title of the summary
   */
  title: string;
};

/** @internal */
export const SummaryConfig$inboundSchema: z.ZodType<
  SummaryConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  action_components: z.array(ActionComponent$inboundSchema),
  alias: types.string(),
  animatic: Animatic$inboundSchema,
  device_icon: EBIcon$inboundSchema,
  display_refs: z.array(DisplayRef$inboundSchema),
  kind: types.optional(types.string()),
  reference_id: types.string(),
  title: types.string(),
}).transform((v) => {
  return remap$(v, {
    "action_components": "actionComponents",
    "device_icon": "deviceIcon",
    "display_refs": "displayRefs",
    "reference_id": "referenceId",
  });
});
/** @internal */
export type SummaryConfig$Outbound = {
  action_components: Array<ActionComponent$Outbound>;
  alias: string;
  animatic: Animatic$Outbound;
  device_icon: EBIcon$Outbound;
  display_refs: Array<DisplayRef$Outbound>;
  kind?: string | undefined;
  reference_id: string;
  title: string;
};

/** @internal */
export const SummaryConfig$outboundSchema: z.ZodType<
  SummaryConfig$Outbound,
  z.ZodTypeDef,
  SummaryConfig
> = z.object({
  actionComponents: z.array(ActionComponent$outboundSchema),
  alias: z.string(),
  animatic: Animatic$outboundSchema,
  deviceIcon: EBIcon$outboundSchema,
  displayRefs: z.array(DisplayRef$outboundSchema),
  kind: z.string().optional(),
  referenceId: z.string(),
  title: z.string(),
}).transform((v) => {
  return remap$(v, {
    actionComponents: "action_components",
    deviceIcon: "device_icon",
    displayRefs: "display_refs",
    referenceId: "reference_id",
  });
});

export function summaryConfigToJSON(summaryConfig: SummaryConfig): string {
  return JSON.stringify(SummaryConfig$outboundSchema.parse(summaryConfig));
}
export function summaryConfigFromJSON(
  jsonString: string,
): SafeParseResult<SummaryConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SummaryConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SummaryConfig' from JSON`,
  );
}
